/* eslint-disable no-unused-vars */
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getRolesFromToken } from '../../helpers';
import { getBackendApiToken } from '../../apis';
import { apiRequest } from '../../components/Auth/authConfig';

interface RolesContextType {
  roles: string[];
  isLoading: boolean;
  error: string | null;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  isAdmin: boolean;
}

const RolesContext = createContext<RolesContextType | undefined>(undefined);

interface RolesProviderProps {
  children: ReactNode;
}

export const RolesProvider: React.FC<RolesProviderProps> = ({ children }) => {
  const [roles, setRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const backendToken = await getBackendApiToken(apiRequest);
        const userRoles = getRolesFromToken(backendToken || '');
        setRoles(userRoles);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch roles');
        setRoles([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const hasRole = (role: string): boolean => {
    return roles.includes(role);
  };

  const hasAnyRole = (requiredRoles: string[]): boolean => {
    return requiredRoles.some(role => roles.includes(role));
  };

  const isAdmin = roles.includes('ROLE_VOUCHER_PORTAL_ADMIN');

  const contextValue: RolesContextType = {
    roles,
    isLoading,
    error,
    hasRole,
    hasAnyRole,
    isAdmin,
  };

  return <RolesContext.Provider value={contextValue}>{children}</RolesContext.Provider>;
};

export const useRoles = (): RolesContextType => {
  const context = useContext(RolesContext);
  if (context === undefined) {
    throw new Error('useRoles must be used within a RolesProvider');
  }
  return context;
};

export default RolesProvider;
